/**
 * @lodex-pro/web-admin — App root (Phase 25 / Task 25.1, DEC-111).
 *
 * Three sequential gates:
 *   1. Auth gate    — `useAuth` (web-shared). Not signed in → <SignInPage>.
 *   2. Role gate    — GET /v1/users/me/profile; only PLATFORM_ADMIN may enter.
 *                     Anyone else sees "Access denied — this console is for
 *                     Paelode administrators only" with a sign-out escape hatch.
 *   3. Shell        — <BrowserRouter> + <AdminShell> + the Phase-25 routes.
 *
 * The role gate is a UX convenience, not a trust boundary — every admin endpoint
 * is independently permission-gated server-side (three-layer model, CLAUDE.md §6).
 */
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useAuth } from '@lodex-pro/web-shared';

import { SignInPage } from './auth/SignInPage';
import { AdminShell } from './AdminShell';
import { useAdminProfile } from './lib/use-profile';

/** Centered message card reusing the auth-shell styling. */
function CenteredCard({
  title,
  body,
  footer,
}: {
  readonly title: string;
  readonly body: string;
  readonly footer?: React.ReactNode;
}): React.JSX.Element {
  return (
    <main className="auth-shell">
      <div className="auth-card" role="alert">
        <div className="auth-brand">
          Paelode&nbsp;Systems <span style={{ color: 'var(--color-link)' }}>Admin</span>
        </div>
        <h1 style={{ fontSize: '18px', margin: '0 0 8px' }}>{title}</h1>
        <p className="auth-subtitle">{body}</p>
        {footer}
      </div>
    </main>
  );
}

export function App(): React.ReactElement {
  const { user, isAuthenticated, loading, signOutAll } = useAuth();
  const profile = useAdminProfile(isAuthenticated && !loading);

  // 1. Auth gate.
  if (loading) {
    return (
      <p role="status" className="auth-shell">
        Loading…
      </p>
    );
  }
  if (!isAuthenticated) {
    return <SignInPage />;
  }

  // 2. Role gate.
  if (profile.isError) {
    return (
      <CenteredCard
        title="Couldn't verify access"
        body="We couldn't load your admin profile. Refresh to try again, or sign out."
        footer={
          <button type="button" className="auth-link" onClick={(): void => void signOutAll()}>
            Sign out
          </button>
        }
      />
    );
  }
  const role = profile.data?.profile.role;
  if (role === undefined) {
    return (
      <p role="status" className="auth-shell">
        Checking access…
      </p>
    );
  }
  if (role !== 'PLATFORM_ADMIN') {
    return (
      <CenteredCard
        title="Access denied"
        body="Access denied — this console is for Paelode administrators only."
        footer={
          <button type="button" className="auth-link" onClick={(): void => void signOutAll()}>
            Sign out
          </button>
        }
      />
    );
  }

  // 3. Authenticated PLATFORM_ADMIN. Identity from the server profile (source of
  // truth) — Amplify signInDetails.loginId is absent on a restored session.
  const userEmail =
    profile.data?.profile.email ?? user?.signInDetails?.loginId ?? user?.username ?? 'admin';
  return (
    <BrowserRouter>
      <AdminShell userEmail={userEmail} role={role} onSignOut={(): void => void signOutAll()} />
    </BrowserRouter>
  );
}
